import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CalendarViewDayOutlinedIcon from '@mui/icons-material/CalendarViewDayOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import TodayIcon from '@mui/icons-material/Today';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlined';
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Link,
  Paper,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import StyledRadio from 'components/controls/StyledRadio';
import SetupDocumentSigningToolbar from 'components/@home/@documents/SentDocuments/edit-steps/SetupDocumentSigningToolbar';
import {
  array,
  bool,
  elementType,
  func,
  node,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from 'prop-types';
import React, { useState } from 'react';
import { I18n } from 'utils/i18n';

/** Light “card” tooltip chrome for rich (marketing) content; plain string tooltips keep default dark style. */
export const richTooltipSlotProps = {
  tooltip: {
    sx: theme => ({
      pointerEvents: 'auto',
      maxWidth: 320,
      bgcolor: 'background.paper',
      color: 'text.primary',
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[8],
      p: 2,
      '& .MuiTooltip-arrow': {
        color: theme.palette.background.paper,
      },
    }),
  },
};

export const OmmnioSignInactiveTooltipContent = ({ marketingUrl }) => {
  if (!marketingUrl) return null;
  return (
    <Stack spacing={1.25} sx={{ maxWidth: 280 }}>
      <Typography
        variant="overline"
        component="p"
        sx={{
          m: 0,
          lineHeight: 1.2,
          color: 'primary.main',
          fontWeight: 700,
          letterSpacing: '0.08em',
        }}
      >
        {I18n.t('HrBot.EditTemplate.SetupDocumentPalette.ommnioSignProductName')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.primary',
          lineHeight: 1.5,
          m: 0,
        }}
      >
        {I18n.t('HrBot.EditTemplate.SetupDocumentPalette.ommnioSignInactivePromo')}
      </Typography>
      <Link
        href={marketingUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={e => e.stopPropagation()}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontSize: '0.875rem',
          fontWeight: 600,
        }}
      >
        {I18n.t('HrBot.EditTemplate.SetupDocumentPalette.ommnioSignInactivePromoLink')}
        <OpenInNewIcon sx={{ fontSize: 16, opacity: 0.85 }} aria-hidden />
      </Link>
    </Stack>
  );
};

OmmnioSignInactiveTooltipContent.propTypes = {
  marketingUrl: string.isRequired,
};

const sectionTitleSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.06,
  color: 'text.secondary',
  textTransform: 'uppercase',
  mb: 1,
};

const PaletteRow = ({
  icon: Icon,
  title,
  caption = null,
  selected = false,
  onClick,
  disabled = false,
  disabledReason = null,
}) => {
  const paper = (
    <Paper
      component="button"
      type="button"
      variant="outlined"
      onClick={() => {
        if (disabled) return;
        onClick();
      }}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.25,
        width: '100%',
        textAlign: 'left',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderColor: selected === true ? 'primary.main' : 'divider',
        bgcolor: selected === true ? 'primary.ultraLight' : 'background.paper',
        opacity: disabled ? 0.45 : 1,
        '&:hover': { borderColor: disabled ? undefined : 'primary.light' },
      }}
    >
      <Box sx={{ color: 'primary.main', pt: 0.25, display: 'flex' }}>
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {caption && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              mt: 0.25,
            }}
          >
            {caption}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  if (disabled && disabledReason) {
    const richTooltip = typeof disabledReason !== 'string';
    return (
      <Tooltip
        title={disabledReason}
        arrow
        placement="right"
        slotProps={
          richTooltip ? richTooltipSlotProps : { tooltip: { sx: { pointerEvents: 'auto' } } }
        }
      >
        <Box component="span" sx={{ display: 'block', width: '100%' }}>
          {paper}
        </Box>
      </Tooltip>
    );
  }

  return paper;
};

PaletteRow.propTypes = {
  icon: elementType.isRequired,
  title: string.isRequired,
  caption: string,
  selected: bool,
  onClick: func.isRequired,
  disabled: bool,
  disabledReason: oneOfType([string, node]),
};

const SetupDocumentPalette = ({
  pendingPlacement = null,
  setPendingPlacement,
  encryptPdfWithDni = false,
  collectiveSigningMode = false,
  canUseXml = false,
  template,
  setTemplate,
  showSharedSigningToolbar = false,
  ommnioSignMarketingUrl,
  digitalSignatureFieldsMode = 'enabled',
  fields,
  placeholderMembers,
  currentSignerIndex,
  setCurrentSignerIndex,
  syncFieldsToTemplate,
}) => {
  const isSelected = next =>
    pendingPlacement &&
    pendingPlacement.kind === next.kind &&
    JSON.stringify(pendingPlacement.preset) === JSON.stringify(next.preset);

  const select = next => {
    if (isSelected(next)) setPendingPlacement(null);
    else setPendingPlacement(next);
  };

  const encryptLocksWidgets = encryptPdfWithDni === true;
  const encryptLockReason = encryptLocksWidgets
    ? I18n.t('HrBot.EditTemplate.SetupDocumentPalette.encryptModeFieldsDisabledTooltip')
    : null;

  const routingLockedByCollective = collectiveSigningMode === true;
  const routingCollectiveReason = routingLockedByCollective
    ? I18n.t('HrBot.EditTemplate.SetupDocumentPalette.collectiveModeRoutingDisabled')
    : null;

  const signaturePaletteHidden = digitalSignatureFieldsMode === 'hidden';
  const signaturePaletteDisabledOnly =
    digitalSignatureFieldsMode === 'disabled' && !signaturePaletteHidden;
  const ommnioSignInactiveTooltip = ommnioSignMarketingUrl ? (
    <OmmnioSignInactiveTooltipContent marketingUrl={ommnioSignMarketingUrl} />
  ) : null;
  /** Firma digital + Desde el perfil: disabled when Ommnio Sign inactive; encrypt mode overrides tooltip. */
  const signAndProfileRowDisabled = encryptLocksWidgets || signaturePaletteDisabledOnly;
  const signAndProfileRowDisabledReason = encryptLocksWidgets
    ? encryptLockReason
    : signaturePaletteDisabledOnly
      ? ommnioSignInactiveTooltip
      : null;

  const sharedToolbarBlock =
    showSharedSigningToolbar && !signaturePaletteHidden ? (
      signaturePaletteDisabledOnly ? (
        <Tooltip
          title={ommnioSignInactiveTooltip}
          arrow
          placement="right"
          slotProps={richTooltipSlotProps}
        >
          <Box sx={{ display: 'block', width: '100%' }}>
            <Box sx={{ opacity: 0.45, pointerEvents: 'none' }}>
              <SetupDocumentSigningToolbar
                template={template}
                setTemplate={setTemplate}
                fields={fields}
                placeholderMembers={placeholderMembers}
                currentSignerIndex={currentSignerIndex}
                setCurrentSignerIndex={setCurrentSignerIndex}
                syncFieldsToTemplate={syncFieldsToTemplate}
                stacked
                disabled
              />
            </Box>
          </Box>
        </Tooltip>
      ) : (
        <SetupDocumentSigningToolbar
          template={template}
          setTemplate={setTemplate}
          fields={fields}
          placeholderMembers={placeholderMembers}
          currentSignerIndex={currentSignerIndex}
          setCurrentSignerIndex={setCurrentSignerIndex}
          syncFieldsToTemplate={syncFieldsToTemplate}
          stacked
        />
      )
    ) : null;

  /** Extraction stays primary when digital-signature palette is unavailable (inactive or other provider). */
  const extractSectionFirst = signaturePaletteDisabledOnly || signaturePaletteHidden;

  const [extractMoreOpen, setExtractMoreOpen] = useState(false);

  const extractSection = (
    <Box>
      <Typography sx={sectionTitleSx}>
        {I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extract')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <PaletteRow
          icon={BadgeIcon}
          title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.employeeCode')}
          caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.employeeCodeHelp')}
          selected={isSelected({ kind: 'item', preset: { name: 'code' } })}
          onClick={() => select({ kind: 'item', preset: { name: 'code' } })}
          disabled={routingLockedByCollective}
          disabledReason={routingCollectiveReason}
        />
        <PaletteRow
          icon={FingerprintIcon}
          title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.dni')}
          caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.dniHelp')}
          selected={isSelected({ kind: 'item', preset: { name: 'dni' } })}
          onClick={() => select({ kind: 'item', preset: { name: 'dni' } })}
          disabled={routingLockedByCollective}
          disabledReason={routingCollectiveReason}
        />
        <Button
          type="button"
          size="small"
          onClick={() => setExtractMoreOpen(o => !o)}
          sx={{
            alignSelf: 'flex-start',
            mt: 0.25,
            px: 0.5,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {extractMoreOpen
            ? I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractShowLess')
            : I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractShowMore')}
        </Button>
        <Collapse in={extractMoreOpen}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <PaletteRow
              icon={DateRangeOutlinedIcon}
              title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractYear')}
              caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractYearHelp')}
              selected={isSelected({ kind: 'item', preset: { name: 'year' } })}
              onClick={() => select({ kind: 'item', preset: { name: 'year' } })}
              disabled={routingLockedByCollective}
              disabledReason={routingCollectiveReason}
            />
            <PaletteRow
              icon={CalendarMonthOutlinedIcon}
              title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractMonth')}
              caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractMonthHelp')}
              selected={isSelected({ kind: 'item', preset: { name: 'month' } })}
              onClick={() => select({ kind: 'item', preset: { name: 'month' } })}
              disabled={routingLockedByCollective}
              disabledReason={routingCollectiveReason}
            />
            <PaletteRow
              icon={CalendarViewDayOutlinedIcon}
              title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractDay')}
              caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.extractDayHelp')}
              selected={isSelected({ kind: 'item', preset: { name: 'day' } })}
              onClick={() => select({ kind: 'item', preset: { name: 'day' } })}
              disabled={routingLockedByCollective}
              disabledReason={routingCollectiveReason}
            />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 260 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        pr: { md: 1 },
      }}
    >
      {canUseXml && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {I18n.t('HrBot.EditTemplate.Wizard.advancedTitle')}
          </Typography>
          <RadioGroup
            row
            value={template.type || 'pdf'}
            onChange={e => setTemplate(t => ({ ...t, type: e.target.value }))}
          >
            <FormControlLabel
              value="pdf"
              control={<StyledRadio />}
              label={I18n.t('HrBot.EditTemplate.Types.pdf')}
            />
            <FormControlLabel
              value="xml"
              control={<StyledRadio />}
              label={I18n.t('HrBot.EditTemplate.Types.xml')}
            />
          </RadioGroup>
        </Paper>
      )}

      {sharedToolbarBlock}

      {extractSectionFirst && extractSection}

      {!signaturePaletteHidden && (
        <>
          <Box>
            <Typography sx={sectionTitleSx}>
              {I18n.t('HrBot.EditTemplate.SetupDocumentPalette.fields')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <PaletteRow
                icon={DriveFileRenameOutlineOutlinedIcon}
                title={I18n.t('Fields.Signature')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.fieldSignatureHelp')}
                selected={isSelected({ kind: 'field', preset: { type: 'signature' } })}
                onClick={() => select({ kind: 'field', preset: { type: 'signature' } })}
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
              <PaletteRow
                icon={CheckBoxOutlinedIcon}
                title={I18n.t('Fields.Checkbox')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.fieldCheckboxHelp')}
                selected={isSelected({ kind: 'field', preset: { type: 'checkbox' } })}
                onClick={() => select({ kind: 'field', preset: { type: 'checkbox' } })}
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
              <PaletteRow
                icon={CalendarTodayOutlinedIcon}
                title={I18n.t('Fields.Date')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.fieldDateHelp')}
                selected={isSelected({ kind: 'field', preset: { type: 'date' } })}
                onClick={() => select({ kind: 'field', preset: { type: 'date' } })}
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
              <PaletteRow
                icon={TextFieldsOutlinedIcon}
                title={I18n.t('Fields.Text')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.fieldTextHelp')}
                selected={isSelected({ kind: 'field', preset: { type: 'text' } })}
                onClick={() => select({ kind: 'field', preset: { type: 'text' } })}
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionTitleSx}>
              {I18n.t('HrBot.EditTemplate.SetupDocumentPalette.fromEmployee')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <PaletteRow
                icon={PersonOutlineIcon}
                title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.fullName')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.prefillHelp')}
                selected={isSelected({
                  kind: 'field',
                  preset: { type: 'text', prefillValue: { field: 'fullName' } },
                })}
                onClick={() =>
                  select({
                    kind: 'field',
                    preset: { type: 'text', prefillValue: { field: 'fullName' } },
                  })
                }
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
              <PaletteRow
                icon={BusinessIcon}
                title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.department')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.prefillHelp')}
                selected={isSelected({
                  kind: 'field',
                  preset: { type: 'text', prefillValue: { field: 'department' } },
                })}
                onClick={() =>
                  select({
                    kind: 'field',
                    preset: { type: 'text', prefillValue: { field: 'department' } },
                  })
                }
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
              <PaletteRow
                icon={WorkOutlineIcon}
                title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.jobTitle')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.prefillHelp')}
                selected={isSelected({
                  kind: 'field',
                  preset: { type: 'text', prefillValue: { field: 'jobTitle' } },
                })}
                onClick={() =>
                  select({
                    kind: 'field',
                    preset: { type: 'text', prefillValue: { field: 'jobTitle' } },
                  })
                }
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
              <PaletteRow
                icon={TodayIcon}
                title={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.todaysDate')}
                caption={I18n.t('HrBot.EditTemplate.SetupDocumentPalette.prefillHelp')}
                selected={isSelected({
                  kind: 'field',
                  preset: { type: 'date', prefillValue: { field: 'currentDate' } },
                })}
                onClick={() =>
                  select({
                    kind: 'field',
                    preset: { type: 'date', prefillValue: { field: 'currentDate' } },
                  })
                }
                disabled={signAndProfileRowDisabled}
                disabledReason={signAndProfileRowDisabledReason}
              />
            </Box>
          </Box>
        </>
      )}

      {!extractSectionFirst && extractSection}

      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          mt: 0.5,
        }}
      >
        {I18n.t('HrBot.EditTemplate.SetupDocumentPalette.placeHint')}
      </Typography>
    </Box>
  );
};

SetupDocumentPalette.propTypes = {
  pendingPlacement: object,
  setPendingPlacement: func.isRequired,
  encryptPdfWithDni: bool,
  collectiveSigningMode: bool,
  canUseXml: bool,
  template: object.isRequired,
  setTemplate: func.isRequired,
  showSharedSigningToolbar: bool,
  digitalSignatureFieldsMode: oneOf(['hidden', 'disabled', 'enabled']),
  fields: array.isRequired,
  placeholderMembers: array.isRequired,
  currentSignerIndex: number.isRequired,
  setCurrentSignerIndex: func.isRequired,
  syncFieldsToTemplate: func.isRequired,
};

export default SetupDocumentPalette;
